import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../../store/slices/authSlice';
import api from '../../services/api';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);

  // Fetch user data periodically to get profile picture updates
  useEffect(() => {
    const fetchUserData = async () => {
      if (token) {
        try {
          const response = await api.get('/user');
          const userData = response.data.user; // Extract user from response
          dispatch(setUser(userData));
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (error) {
          console.error('Failed to fetch user data:', error);
        }
      }
    };

    // Only fetch if user data doesn't exist
    if (token && !user) {
      fetchUserData();
    }
    
    // Refresh user data every 2 minutes to catch profile picture updates (less frequent)
    const interval = setInterval(fetchUserData, 120000);
    
    return () => clearInterval(interval);
  }, [token, dispatch, user]);

  const handleLinkClick = () => {
    // Close mobile menu when link is clicked
    if (onClose) {
      onClose();
    }
  };

  const isActive = (path) => {
    return location.pathname === path
      ? 'bg-luxury-gold text-white'
      : 'text-gray-300 hover:bg-luxury-darkNavy hover:text-white';
  };

  const getMenuItems = () => {
    const role = user?.role;
    
    switch (role) {
      case 'passenger':
        return [
          { path: '/passenger/dashboard', icon: '📊', label: 'Dashboard' },
          { path: '/passenger/bookings', icon: '🚗', label: 'My Bookings' },
          { path: '/booking', icon: '➕', label: 'New Booking' },
          { path: '/passenger/profile', icon: '👤', label: 'Profile' },
        ];
      case 'driver':
        return [
          { path: '/driver/dashboard', icon: '📊', label: 'Dashboard' },
          { path: '/driver/rides', icon: '🚗', label: 'My Rides' },
          { path: '/driver/submit-vehicle', icon: '🚙', label: 'Submit Vehicle' },
          { path: '/driver/earnings', icon: '💰', label: 'Earnings' },
          { path: '/driver/ratings', icon: '⭐', label: 'Ratings & Reviews' },
          { path: '/driver/profile', icon: '👤', label: 'Profile' },
        ];
      case 'admin':
        return [
          { path: '/admin/dashboard', icon: '📊', label: 'Dashboard' },
          { path: '/admin/verifications', icon: '✅', label: 'Verifications' },
          { path: '/admin/profile-pictures', icon: '🖼️', label: 'Profile Pictures' },
          { path: '/admin/analytics', icon: '📈', label: 'Analytics' },
          { path: '/admin/site-analytics', icon: '🌐', label: 'Site Analytics' },
          { path: '/admin/users', icon: '👥', label: 'Users' },
          { path: '/admin/drivers', icon: '🚕', label: 'Drivers' },
          { path: '/admin/rides', icon: '🚗', label: 'Rides' },
          { path: '/admin/offline-booking', icon: '📞', label: 'Offline Booking' },
          { path: '/admin/vehicles', icon: '🚙', label: 'Vehicles' },
          { path: '/admin/payments', icon: '💳', label: 'Payments' },
          { path: '/admin/rates', icon: '💵', label: 'Rates' },
          { path: '/admin/commission', icon: '💰', label: 'Commission' },
          { path: '/admin/timezone', icon: '🌍', label: 'Timezone' },
          { path: '/admin/registration', icon: '🔒', label: 'Registration' },
          { path: '/admin/maintenance', icon: '⚠️', label: 'Maintenance' },
          { path: '/admin/ratings', icon: '⭐', label: 'Ratings & Reviews' },
          { path: '/admin/bloggers', icon: '✍️', label: 'Manage Bloggers' },
          { path: '/admin/blog-posts', icon: '📝', label: 'Blog Posts' },
          { path: '/admin/campaign-links', icon: '🔗', label: 'Campaign Links' },
          { path: '/admin/profile', icon: '👤', label: 'Profile' },
        ];
      case 'blogger':
        return [
          { path: '/blogger/dashboard', icon: '📊', label: 'Dashboard' },
          { path: '/blogger/posts', icon: '📝', label: 'My Posts' },
          { path: '/blog', icon: '🌐', label: 'View Blog' },
          { path: '/blogger/campaign-links', icon: '🔗', label: 'Campaign Links' },
          { path: '/blogger/profile', icon: '👤', label: 'Profile' },
        ];
      default:
        return [];
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        ></div>
      )}
      
      {/* Sidebar */}
      <aside className={`bg-luxury-navy w-64 h-screen flex flex-col fixed lg:relative z-50 transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
      {/* Header with Logo */}
      <div className="p-6 border-b border-gray-700 flex-shrink-0">
        <div className="flex justify-between items-center mb-4">
          <div>
            <Link to="/" onClick={handleLinkClick}>
              <img
                src="/Ifesteimo-Transport-logo.png"
                alt="Ifesteimo Transport"
                className="h-27 w-auto object-contain"
              />
            </Link>
            <p className="text-gray-400 text-sm -mt-10">
              {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)} Panel
            </p>
          </div>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <svg className="h-6 w-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* User Profile Card - Moved to Top */}
        <div className="flex items-center space-x-3 bg-gray-800 bg-opacity-50 px-3 py-3 rounded-lg">
          {user?.profile_image && (user?.role !== 'driver' || user?.profile_picture_status === 'approved') ? (
            <img
              src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000'}/storage/${user.profile_image}`}
              alt={user?.name}
              className="w-12 h-12 rounded-full object-cover border-2 border-luxury-gold"
              onError={(e) => {
                e.target.onerror = null;
                const bgColor = user?.role === 'driver' ? 'B8860B' : user?.role === 'admin' ? '1F2937' : '3B82F6';
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=${bgColor}&color=fff&size=48`;
              }}
            />
          ) : (
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg border-2 ${
              user?.role === 'driver' ? 'bg-luxury-gold border-luxury-gold' : 
              user?.role === 'admin' ? 'bg-gray-700 border-gray-600' : 
              'bg-blue-500 border-blue-400'
            }`}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            <p className="text-white font-medium text-sm truncate">{user?.name}</p>
            <p className="text-gray-400 text-xs truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-2 sidebar-scroll">
        {getMenuItems().map((item) => (
          <Link
            key={item.path}
            to={item.path}            onClick={handleLinkClick}            className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${isActive(
              item.path
            )}`}
          >
            <span className="text-xl">{item.icon}</span>
            <span className="font-medium">{item.label}</span>
          </Link>
        ))}
      </nav>

      {/* Contact Support — passengers & drivers only */}
      {(user?.role === 'passenger' || user?.role === 'driver') && (
        <div className="flex-shrink-0 px-4 py-4 border-t border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3 px-1">
            Need Help?
          </p>
          <a
            href="tel:+13109514000"
            onClick={handleLinkClick}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-colors group"
          >
            <span className="w-8 h-8 rounded-lg bg-green-900/50 group-hover:bg-green-800 flex items-center justify-center flex-shrink-0 transition-colors">
              <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </span>
            <div className="overflow-hidden">
              <p className="text-xs text-gray-500 leading-none mb-0.5">Call us</p>
              <p className="text-sm font-medium truncate">+1-310-951-4000</p>
            </div>
          </a>
          <a
            href="mailto:reservation@ifesteimo.com"
            onClick={handleLinkClick}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-colors group mt-1"
          >
            <span className="w-8 h-8 rounded-lg bg-blue-900/50 group-hover:bg-blue-800 flex items-center justify-center flex-shrink-0 transition-colors">
              <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </span>
            <div className="overflow-hidden">
              <p className="text-xs text-gray-500 leading-none mb-0.5">Email us</p>
              <p className="text-sm font-medium truncate">reservation@ifesteimo.com</p>
            </div>
          </a>
        </div>
      )}
    </aside>
    </>
  );
};

export default Sidebar;
